import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, ArrowRight } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();

  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate(lp("/"));
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n > 8) {
        setStatus("failed");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setAttempts(n);
        setTimeout(() => poll(n + 1), 2000);
      } catch (_e) {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="container py-16 md:py-24 max-w-2xl">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass rounded-3xl p-10 md:p-14 text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-gold mx-auto animate-spin" />
            <p className="mt-8 font-display text-card-lg">{t.checkout.checking}</p>
            <p className="mt-2 text-caption text-cream-dim">attempt {attempts + 1}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-gold" />
            </div>
            <h1 className="mt-8 font-display text-section md:text-section-lg tracking-tight">
              {t.checkout.successTitle}
            </h1>
            <p className="mt-5 text-cream-muted text-body">{t.checkout.successSub}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to={lp("/dashboard")} className="btn btn-gold">
                {t.checkout.goDashboard} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={lp("/guides")} className="btn btn-ghost">
                {t.checkout.backToGuides}
              </Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <h1 className="font-display text-card-lg">
              {lang === "en" ? "We couldn't confirm the payment." : "Nu am putut confirma plata."}
            </h1>
            <p className="mt-4 text-cream-muted">
              {lang === "en" ? "Please check your email or contact " : "Verifică-ți emailul sau scrie-ne la "}
              <a href="mailto:hello@thelifeplot.com" className="text-gold">hello@thelifeplot.com</a>.
            </p>
            <Link to={lp("/guides")} className="btn btn-gold mt-10 inline-flex">
              {t.checkout.backToGuides}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
